import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "@/components/ui/dialog";
import { DialogHeaderBlock } from "@/components/patterns/headers";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Plus, Minus, RefreshCw, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Select, SelectValue, SelectTrigger, SelectContent, SelectItem } from "../ui/select";
import { PROMPT_TYPES } from "@/lib/constants/prompts";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import { LoadingButton } from "../ui/custom/loading-button";
import { getPromptEditorAutofillProps } from "@/lib/prompt-input";
import { toast } from "sonner";
import { useTRPC } from "@/lib/trpc";
import { useSupabaseBroadcast } from "@/hooks/use-supabase-broadcast";

interface GeneratePromptsDialogProps {
  isOpen: boolean;
  searchSpecificationId: string;
  setIsOpen: (isOpen: boolean) => void;
}

interface GeneratedPrompt {
  id: string;
  text: string;
  promptType: "organic" | "competitor" | "brand_specific";
  language: string;
}

type PromptGenerationBroadcastStage = "GENERATING" | "COMPLETED" | "FAILED";

type PromptGenerationBroadcastPayload = {
  generationId?: string;
  searchSpecificationId?: string;
  mode?: "STANDARD" | "ONBOARDING";
  stage?: PromptGenerationBroadcastStage;
  prompts?: Array<{ text: string; promptType: "ORGANIC" | "COMPETITOR" | "BRAND_SPECIFIC" }>;
  error?: string;
};

const toPromptType = (promptType: "ORGANIC" | "COMPETITOR" | "BRAND_SPECIFIC") =>
  promptType.toLowerCase().replace(/_/g, "_") as "organic" | "competitor" | "brand_specific";

export function GeneratePromptsDialog({
  isOpen,
  setIsOpen,
  searchSpecificationId,
}: GeneratePromptsDialogProps) {
  const trpc = useTRPC();
  const [quantity, setQuantity] = useState(3);
  const [selectedType, setSelectedType] = useState<string>("organic");
  const [generatedPrompts, setGeneratedPrompts] = useState<GeneratedPrompt[]>([]);
  const [regeneratingPrompts, setRegeneratingPrompts] = useState<Set<string>>(new Set());
  const [deletingPrompts, setDeletingPrompts] = useState<Set<string>>(new Set());
  const [pendingGenerationIds, setPendingGenerationIds] = useState<Set<string>>(new Set());
  const generationIdToPromptIdRef = useRef<Map<string, string>>(new Map());

  const generatePromptsMutation = useMutation(trpc.prompts.generatePrompts.mutationOptions({}));

  useEffect(() => {
    if (!isOpen) {
      setPendingGenerationIds(new Set());
      setRegeneratingPrompts(new Set());
      generationIdToPromptIdRef.current.clear();
    }
  }, [isOpen]);

  useSupabaseBroadcast({
    channelName: `prompt_generation:${searchSpecificationId}`,
    event: "prompt_generation_progress",
    callbackFn: (broadcastMessage: { payload?: PromptGenerationBroadcastPayload }) => {
      const payload = broadcastMessage.payload;
      if (!payload?.generationId || !payload.searchSpecificationId) return;
      if (payload.searchSpecificationId !== searchSpecificationId) return;
      if (payload.stage === "GENERATING") return;

      const promptId = generationIdToPromptIdRef.current.get(payload.generationId);

      if (payload.stage === "FAILED") {
        if (promptId) {
          generationIdToPromptIdRef.current.delete(payload.generationId);
          setRegeneratingPrompts((prev) => {
            const next = new Set(prev);
            next.delete(promptId);
            return next;
          });
        }

        setPendingGenerationIds((prev) => {
          const next = new Set(prev);
          next.delete(payload.generationId!);
          return next;
        });

        toast.error(payload.error ?? "Failed to generate prompts");
        return;
      }

      if (payload.stage !== "COMPLETED") return;

      if (promptId) {
        const regeneratedPrompt = payload.prompts?.[0];
        if (!regeneratedPrompt) {
          toast.error("Prompt regeneration returned no result");
        } else {
          setGeneratedPrompts((prev) =>
            prev.map((prompt) =>
              prompt.id === promptId
                ? {
                    ...prompt,
                    text: regeneratedPrompt.text,
                  }
                : prompt,
            ),
          );
        }

        generationIdToPromptIdRef.current.delete(payload.generationId);
        setRegeneratingPrompts((prev) => {
          const next = new Set(prev);
          next.delete(promptId);
          return next;
        });
      } else {
        const prompts = payload.prompts ?? [];
        if (prompts.length === 0) {
          toast.error("Prompt generation returned no prompts");
        } else {
          const mappedPrompts = prompts.map((prompt, index) => ({
            id: `temp-${Date.now()}-${index}-${crypto.randomUUID()}`,
            text: prompt.text,
            promptType: toPromptType(prompt.promptType),
            language: "en",
          }));
          setGeneratedPrompts((prev) => [...prev, ...mappedPrompts]);
        }
      }

      setPendingGenerationIds((prev) => {
        const next = new Set(prev);
        next.delete(payload.generationId!);
        return next;
      });
    },
  });

  const handleGenerate = async () => {
    try {
      const { generationId } = await generatePromptsMutation.mutateAsync({
        searchSpecificationId,
        count: quantity,
        promptType: selectedType.toUpperCase().replace(/_/g, "_") as
          | "ORGANIC"
          | "COMPETITOR"
          | "BRAND_SPECIFIC",
        saveToDb: false,
      });

      setPendingGenerationIds((prev) => new Set(prev).add(generationId));
    } catch (error) {
      console.error("Failed to start prompt generation:", error);
      toast.error("Failed to generate prompts. Please try again.");
    }
  };

  const handleDeletePrompt = async (id: string) => {
    setDeletingPrompts((prev) => new Set(prev).add(id));
    await new Promise((resolve) => setTimeout(resolve, 100));
    setGeneratedPrompts((prev) => prev.filter((p) => p.id !== id));
    setDeletingPrompts((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  const queryClient = useQueryClient();
  const invalidatePromptQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["prompts"] });
    queryClient.invalidateQueries({ queryKey: trpc.prompts.getSearchSpecificationPromptList.queryKey() });
    queryClient.invalidateQueries({ queryKey: trpc.prompts.getSearchSpecificationPromptMetrics.queryKey() });
    queryClient.invalidateQueries({ queryKey: trpc.prompts.getSearchSpecificationPrompts.queryKey() });
    queryClient.invalidateQueries({
      queryKey: trpc.searchSpecifications.getSearchSpecificationPromptsCount.queryKey({
        id: searchSpecificationId,
      }),
    });
  };

  const createPromptsMutation = useMutation(
    trpc.prompts.createPrompts.mutationOptions({
      onSuccess: () => {
        setIsOpen(false);
        toast.success("Prompts saved successfully");
        invalidatePromptQueries();
      },
    }),
  );

  const handleRegeneratePrompt = async (id: string) => {
    const prompt = generatedPrompts.find((p) => p.id === id);
    if (!prompt) return;

    setRegeneratingPrompts((prev) => new Set(prev).add(id));

    try {
      const { generationId } = await generatePromptsMutation.mutateAsync({
        searchSpecificationId,
        count: 1,
        promptType: prompt.promptType.toUpperCase().replace(/_/g, "_") as
          | "ORGANIC"
          | "COMPETITOR"
          | "BRAND_SPECIFIC",
        saveToDb: false,
      });

      generationIdToPromptIdRef.current.set(generationId, id);
      setPendingGenerationIds((prev) => new Set(prev).add(generationId));
    } catch (error) {
      console.error("Failed to regenerate prompt:", error);
      toast.error("Failed to regenerate prompt. Please try again.");
      setRegeneratingPrompts((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleSavePrompts = async () => {
    await createPromptsMutation.mutateAsync({
      prompts: generatedPrompts.map((p) => ({
        text: p.text,
        promptType: p.promptType.toUpperCase().replace(/_/g, "_") as
          | "ORGANIC"
          | "COMPETITOR"
          | "BRAND_SPECIFIC",
        searchSpecificationId,
        status: "ACTIVE" as const,
      })),
    });
  };

  const hasPendingGenerations = pendingGenerationIds.size > 0;

  return (
    <Dialog open={isOpen} modal onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeaderBlock title="Generate Prompts" />

        <div className="space-y-6">
          <div className="flex items-end justify-center space-x-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="quantity" variant="brand">
                Number of prompts
              </Label>
              <div className="flex items-center space-x-2">
                <Button
                  variant="brandOutline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1 || generatePromptsMutation.isPending || hasPendingGenerations}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  id="quantity"
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                  variant="brand"
                  className="w-20 text-center [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                  min={1}
                  max={10}
                  disabled={generatePromptsMutation.isPending || hasPendingGenerations}
                />
                <Button
                  variant="brandOutline"
                  size="icon"
                  onClick={() => setQuantity(Math.min(10, quantity + 1))}
                  disabled={quantity >= 10 || generatePromptsMutation.isPending || hasPendingGenerations}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="type" variant="brand">
                Prompt type
              </Label>
              <Select
                value={selectedType}
                onValueChange={setSelectedType}
                disabled={generatePromptsMutation.isPending || hasPendingGenerations}
              >
                <SelectTrigger variant="brand">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROMPT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value || ""}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <LoadingButton
              variant="brand"
              onClick={handleGenerate}
              disabled={generatePromptsMutation.isPending || hasPendingGenerations}
              loading={generatePromptsMutation.isPending}
            >
              Generate Prompts
            </LoadingButton>
          </div>

          {hasPendingGenerations && generatedPrompts.length === 0 ? (
            <div className="flex min-h-[120px] items-center justify-center gap-2 rounded-lg border border-dashed border-brand-ink/10 bg-brand-ink/[0.015] p-6 text-sm text-brand-ink/40">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Generating prompts...
            </div>
          ) : null}

          {generatedPrompts.length > 0 && (
            <>
              <Separator className="bg-brand-ink/8" />
              <div className="space-y-4">
                <div className="space-y-2">
                  {generatedPrompts.map((prompt, index) => {
                    const isRegenerating = regeneratingPrompts.has(prompt.id);
                    const isDeleting = deletingPrompts.has(prompt.id);
                    const isDisabled = isRegenerating || isDeleting || hasPendingGenerations;

                    return (
                      <div key={prompt.id} className="flex items-center space-x-2">
                        <Input
                          {...getPromptEditorAutofillProps(`generated-prompt-${index + 1}`)}
                          value={prompt.text}
                          onChange={(e) => {
                            setGeneratedPrompts((prev) =>
                              prev.map((p) =>
                                p.id === prompt.id ? { ...p, text: e.target.value } : p,
                              ),
                            );
                          }}
                          placeholder="Generated prompt text..."
                          variant="brand"
                          className="flex-1"
                          disabled={isDisabled}
                        />
                        <Badge
                          variant="secondary"
                          className="whitespace-nowrap border-0 bg-brand-ink/[0.06] text-brand-ink/60"
                        >
                          {PROMPT_TYPES.find((t) => t.value === prompt.promptType)?.label ||
                            "Unknown"}
                        </Badge>
                        <Button
                          variant="brandGhost"
                          size="sm"
                          onClick={() => handleRegeneratePrompt(prompt.id)}
                          disabled={generatePromptsMutation.isPending || isDisabled}
                        >
                          <RefreshCw
                            className={`h-4 w-4 ${isRegenerating ? "animate-spin" : ""}`}
                          />
                        </Button>
                        <Button
                          variant="brandDestructiveGhost"
                          size="sm"
                          onClick={() => handleDeletePrompt(prompt.id)}
                          disabled={isDisabled}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
        <Separator className="bg-brand-ink/8" />
        <DialogFooter>
          <Button
            variant="brandOutline"
            className="mr-auto"
            onClick={() => setIsOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="brand"
            onClick={handleSavePrompts}
            disabled={
              generatedPrompts.length === 0 ||
              hasPendingGenerations ||
              regeneratingPrompts.size > 0 ||
              createPromptsMutation.isPending
            }
          >
            Save Prompts
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
